/**
 * 家具・人物の簡易当たり判定。
 *
 * 壁は layout.ts の歩行可能領域で判定する一方、家具まで同じ仕組みに混ぜると
 * 章データの配置変更のたびにレイアウトを書き直す必要がある。そこで、床を占有する
 * 調査対象だけを XZ の矩形に変換し、プレイヤーの円との距離で判定する。
 */

import type { Interactable } from '../types/scenario';
import { defaultSize } from './sceneGraph';
import { roomLocalToWorld, type OfficeLayout } from './layout';
import { CHAIR_SIZE, chairOffsetZ, sharedDeskFor, sharedChairOffset } from './furnitureLayout';

export interface CollisionObstacle {
  id: string;
  x0: number;
  x1: number;
  z0: number;
  z1: number;
  /** 階の床高。上下階で同じ XZ にある家具を誤って拾わないために使う。 */
  floorY: number;
}

const COLLIDABLE_VISUALS = new Set<Interactable['visual']>([
  'desk',
  'pc',
  'person',
  'server-rack',
  'printer',
  'microwave',
]);

/** 見た目が実際に床を占める幅・奥行き。PC は付属の机まで含める。 */
export function obstacleFootprint(item: Interactable): [number, number] | null {
  if (!COLLIDABLE_VISUALS.has(item.visual)) return null;
  const [width, , depth] = item.size ?? defaultSize(item.visual);
  if (item.visual === 'pc') {
    return [Math.max(1, width + 0.7), Math.max(0.6, depth + 0.4)];
  }
  if (item.visual === 'person') {
    // 人の当たり判定は肩幅より少し細くし、横を自然にすれ違えるようにする。
    return [width * 0.76, depth * 0.76];
  }
  return [width, depth];
}

export function buildCollisionObstacles(
  layout: OfficeLayout,
  interactables: Interactable[],
): CollisionObstacle[] {
  const obstacles: CollisionObstacle[] = [];
  for (const item of interactables) {
    const footprint = obstacleFootprint(item);
    const room = layout.rooms[item.roomId];
    if (!footprint || !room) continue;
    const [x, , z] = roomLocalToWorld(room, item.position);
    const [width, depth] = footprint;
    const sharedDesk = item.visual === 'pc' ? sharedDeskFor(item, interactables) : undefined;
    if (!sharedDesk) obstacles.push({
      id: item.id,
      x0: x - width / 2,
      x1: x + width / 2,
      z0: z - depth / 2,
      z1: z + depth / 2,
      floorY: room.y,
    });
    if (item.visual === 'pc') {
      const chairZ = z + (sharedDesk ? sharedChairOffset(item, sharedDesk) : chairOffsetZ(depth));
      obstacles.push({ id: `${item.id}:chair`,
        x0: x - CHAIR_SIZE[0] / 2, x1: x + CHAIR_SIZE[0] / 2,
        z0: chairZ - CHAIR_SIZE[2] / 2, z1: chairZ + CHAIR_SIZE[2] / 2, floorY: room.y });
    }
  }
  // Low storage added beneath the exterior windows in officeDetails.ts.
  for (const room of Object.values(layout.rooms)) {
    if (/eps|server|stairs/.test(room.id)) continue;
    const width = Math.min(2.4, room.width - 1.1);
    const north = room.center[2] < 0;
    const z = north ? room.rect.z0 + 0.195 : room.rect.z1 - 0.195;
    obstacles.push({ id: `storage:${room.id}`, x0: room.center[0] - width / 2,
      x1: room.center[0] + width / 2, z0: z - 0.11, z1: z + 0.11, floorY: room.y });
  }
  return obstacles;
}

/** 半径を持つプレイヤーの円が家具の矩形と重なるか。 */
export function collidingObstacle(
  obstacles: readonly CollisionObstacle[],
  x: number,
  z: number,
  floorY: number,
  radius: number,
): CollisionObstacle | null {
  for (const obstacle of obstacles) {
    if (Math.abs(obstacle.floorY - floorY) > 0.65) continue;
    const nearestX = Math.max(obstacle.x0, Math.min(x, obstacle.x1));
    const nearestZ = Math.max(obstacle.z0, Math.min(z, obstacle.z1));
    const dx = x - nearestX;
    const dz = z - nearestZ;
    if (dx * dx + dz * dz < radius * radius) return obstacle;
  }
  return null;
}
